import abTests from 'frontend/shared/javascripts/modules/ab_tests'
import session from 'frontend/shared/javascripts/util/session'
import { track } from 'frontend/shared/javascripts/tracking'
import { onConsentReady } from 'frontend/shared/javascripts/util/gdpr_consent'
import { appVersionAtLeast, isGoogleBot, isNativeApp } from 'shared/javascripts/util/detection'
import { deviceDetector } from 'shared/javascripts/util/is_mobile'

export interface LearningRemindersData {
  context: 'video_player' | 'homepage' | 'thank_you' | 'other'
  remindAgain: number
  lastCompletedStep: string
  internalNavigation: boolean
  referrer?: string
  appRating: boolean
}

/** How a frontend opens the overlays. The reminders have already counted them as seen. */
export interface ReminderOverlays {
  openAppRating: () => void
  openAppAdvertisement: () => void
}

export function eligibleForAppRatingOverlay(): boolean {
  return appVersionAtLeast(1) && !session.appRatingOverlaySeen && session.seenVideos >= 3
}

function eligibleForAppAdvertisement(): boolean {
  if (isNativeApp()) return false
  if (deviceDetector.isTablet()) return false

  return !!session.signedIn && !session.nativeAppAdvertisementSeen
}

function showAppRating(overlays: ReminderOverlays): void {
  session.appRatingOverlaySeen = true
  // tracking: app_rating.shown
  track('appRating.overlaySeen')
  overlays.openAppRating()
}

function showAppAdvertisement(overlays: ReminderOverlays): void {
  if (!eligibleForAppAdvertisement()) return

  session.nativeAppAdvertisementSeen = true
  // Not in the variation of the A/B test user_home_hub. Counted as seen, so it doesn't turn up on a later page.
  if (abTests.variantCurrentlyActive('user_home_hub', 'b')) return

  overlays.openAppAdvertisement()
}

/** One per page load: it remembers across navigations whether a reminder was shown already. */
export function createLearningReminders() {
  let advertisementShown = false
  let firstVisit = true

  function fire(
    data: LearningRemindersData,
    overlays: ReminderOverlays,
    { bannerFired }: { bannerFired: boolean },
  ): void {
    function showAdvertisement(): void {
      session.remindAgain = data.remindAgain
      advertisementShown = true
      showAppAdvertisement(overlays)
    }

    if (bannerFired && firstVisit) {
      // do nothing
    } else if (isGoogleBot()) {
      // do nothing
    } else if (data.appRating && eligibleForAppRatingOverlay()) {
      showAppRating(overlays)
    } else if (data.context === 'video_player' || session.lastCompletedStep !== 'none') {
      // on the video page; or user is already in the signup flow
      if (session.remindAgain! < 1) {
        if (data.internalNavigation || session.remindAgain! < 0) {
          // internal navigation or overdue
          showAdvertisement()
        } else if (session.remindAgain === 0) {
          // user came from outside, so delay once
          session.remindAgain = 1
        }
      }
    } else if (isNativeApp()) {
      if (
        session.lastCompletedStep === 'none' &&
        data.context === 'homepage' &&
        !advertisementShown &&
        !data.referrer
      ) {
        // user has just opened the native app
        showAdvertisement()
      }
    }

    firstVisit = false
  }

  /** Call on every page. Returns a function that stops waiting for the consent banner. */
  return function remind(data: LearningRemindersData, overlays: ReminderOverlays): () => void {
    if (data.context === 'thank_you') {
      session.remindAgain = -1
      return () => {}
    }

    if (session.remindAgain === null) session.remindAgain = data.remindAgain
    if (session.lastCompletedStep === null || session.lastCompletedStep === 'none') {
      session.lastCompletedStep = data.lastCompletedStep
    }

    return onConsentReady((consent) => fire(data, overlays, consent))
  }
}
